import { Plus } from "lucide-react";

import type { BriefMemory } from "../hooks/useBriefMemory";
import type { AssetFileRef } from "./upload-assets/types";

import { AssetThumb } from "./AssetThumb";

/**
 * Flatten every asset file in the brief (shared + per-variation), deduped by
 * `attachmentId`. Entries missing `attachmentId` or `url` are skipped —
 * `assetFileSchema` rejects them, so the brief should never hold one, and a
 * tile could not render it anyway.
 */
function collectAssetFiles(memory: BriefMemory | null): AssetFileRef[] {
  const seen = new Set<number>();
  const out: AssetFileRef[] = [];

  const push = (raw: unknown) => {
    const files = (raw as { files?: unknown } | undefined)?.files;

    if (!Array.isArray(files)) return;

    for (const entry of files) {
      const file = entry as Record<string, unknown>;

      if (
        typeof file.attachmentId !== "number" ||
        typeof file.url !== "string" ||
        seen.has(file.attachmentId)
      ) {
        continue;
      }
      seen.add(file.attachmentId);

      out.push({
        attachmentId: file.attachmentId,
        mime: typeof file.mime === "string" ? file.mime : null,
        name: typeof file.name === "string" ? file.name : "file",
        size: typeof file.size === "number" ? file.size : null,
        url: file.url,
      });
    }
  };

  push(memory?.assets);
  const variations = Array.isArray(memory?.variations) ? memory.variations : [];

  for (const variation of variations) {
    push((variation as { assets?: unknown } | undefined)?.assets);
  }

  return out;
}

type UploadedFilesPanelProps = {
  memory: BriefMemory | null;
  /** Opens the upload modal (shared with the chat's attach button). */
  onAddAssets: () => void;
};

export function UploadedFilesPanel({
  memory,
  onAddAssets,
}: UploadedFilesPanelProps) {
  const assets = collectAssetFiles(memory);

  return (
    <aside className="w-full max-w-[320px] rounded-2xl border border-border-secondary bg-bg-primary p-xl shadow-sm">
      <h2 className="mb-lg text-md font-semibold text-text-primary">
        Uploaded files
      </h2>

      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-text-primary">Assets</span>
        <button
          className="flex items-center gap-1 text-sm font-semibold text-fg-brand-primary outline-hidden transition-colors hover:underline"
          onClick={onAddAssets}
          type="button"
        >
          <Plus className="size-4" />
          Add files
        </button>
      </div>

      {assets.length > 0 ? (
        <div className="mt-md grid grid-cols-3 gap-sm">
          {assets.map((file) => (
            <AssetThumb file={file} key={file.attachmentId} />
          ))}
        </div>
      ) : (
        <p className="mt-md text-xs text-text-tertiary">
          No assets yet. Use “Add files” to upload.
        </p>
      )}
    </aside>
  );
}
